export const ROLE_LABELS = {
  Administrator: 'Facility Administration',
  Caregiver: 'Caregiver / Direct Care Staff',
};

export const STAFF_TYPE_LABELS = {
  Administrator: 'Facility Administrators',
  Caregiver: 'Caregivers / Direct Care Staff',
};

export const TRAINING_LABELS = {
  initial_training: 'Initial Training',
  continuing_education: 'Annual Training',
  initial_certification: 'Initial Certification',
  recertification: 'Recertification',
  recertification_20hr: 'Recertification (20h)',
  recertification_20hr_live: 'Recertification (20h Live)',
};

export const FACILITY_TYPE_CODES = {
  elderly: 'RCFE',
  group: 'GH',
  adult: 'ARF',
  short_term: 'STRTP',
};

export const INDIVIDUAL_VARIANTS = {
  'Caregiver|continuing_education': {
    subscription: {
      header: 'Subscribe & Save – Full Access',
      subheader: 'Stay compliant year-round with automatic renewal, ongoing training access, and less stress around Annual Training deadlines.',
      priceSuffix: 'per year',
    },
    oneTime: {
      header: 'One-Time Training Bundle',
      subheader: 'Complete your required Annual Training for the current calendar year only.',
      priceSuffix: 'one-time',
    },
  },
  'Caregiver|initial_training': {
    subscription: {
      header: 'Subscribe & Save – Full Access',
      subheader: 'Complete Initial Training now and maintain ongoing access for future Annual Training requirements.',
      priceSuffix: 'per year',
    },
    oneTime: {
      header: 'One-Time Training Bundle',
      subheader: 'Complete your required Initial Training with one-time access only.',
      priceSuffix: 'one-time',
    },
  },
  'Administrator|recertification': {
    hero: {
      heading: 'Choose how you want to recertify',
      subtitle: 'Same 40 hours. Very different value.',
    },
    summaryLabel: '40-Hour Recertification',
    subscription: {
      header: '40-Hour Subscription',
      subheader: 'Your outsourced compliance team — all year long.',
      priceSuffix: '/ license cycle',
      featuresLabel: 'EVERYTHING IN THE BUNDLE, PLUS:',
      features: [
        { included: true, text: '20 hrs Live + 20 hrs Self-Paced', detail: 'full 40-hour recertification' },
        { included: true, text: 'Monthly meal planners', detail: 'Registered Dietician approved', badge: '$1,200 value', badgeVariant: 'yellow' },
        { included: true, text: 'Monthly activity planners', detail: 'ready to implement in your facility' },
        { included: true, text: 'RCFE Consultant access', detail: 'on-demand expert help (RCFE only)' },
        { included: true, text: 'Caregiver training discounts', detail: '10% off 1st employee, 15% off at 5+' },
        { included: true, text: 'Automatic renewal', detail: 'never miss a deadline again' },
      ],
      callout: 'Cancel anytime. Most admins save 3–5× the subscription price through the caregiver discount alone.',
    },
    oneTime: {
      header: '40-Hour Bundle',
      subheader: 'Just the CEU hours. Nothing extra.',
      priceSuffix: 'one-time',
      featuresLabel: "WHAT'S INCLUDED",
      features: [
        { included: true,  text: '20 hrs Live + 20 hrs Self-Paced', detail: 'full 40-hour recertification' },
        { included: false, text: 'No meal planners' },
        { included: false, text: 'No activity planners' },
        { included: false, text: 'No RCFE Consultant access' },
        { included: false, text: 'No caregiver training discounts' },
        { included: false, text: 'No automatic renewal', detail: 'you re-buy each cycle' },
      ],
    },
  },
  'Administrator|initial_certification': {
    subscription: {
      header: 'Subscribe & Save – Full Access',
      subheader: 'Your annual subscription includes the required Initial Certification training and continued access to Title 22 CEUs for upcoming recertification cycles.',
      priceSuffix: 'today',
    },
    oneTime: {
      header: 'One-Time CEU Bundle',
      subheader: 'Complete your Initial Certification with 12-month access. Continued CEU access is not included.',
      priceSuffix: 'one-time',
    },
  },
  'Administrator|recertification_20hr': {
    hero: {
      heading: 'Your 20-hour self-paced path',
      subtitle: 'Self-paced CEUs to complete your license cycle.',
    },
    summaryLabel: '20-Hour Self-Paced Recertification',
    oneTime: {
      header: '20-Hour Self-Paced Bundle',
      subheader: ({ facilityCode }) =>
        `Complete Title 22 CEUs for ${facilityCode || 'RCFE / ARF'} Administrator recertification on your own schedule.`,
      switchLink: {
        label: 'Need the full self-paced + live package? Select the 40H Bundle here',
        to: '/signup/individual/training',
      },
      priceSuffix: 'one-time',
      featuresLabel: "WHAT'S INCLUDED",
      features: [
        { included: true,  text: '20 hrs Self-Paced', detail: 'RCFE or ARF Administrator CEUs' },
        { included: true,  text: 'CDSS Approved', detail: 'meets Title 22 requirements' },
        { included: true,  text: 'On-demand access', detail: 'start and finish anytime' },
        { included: false, text: 'No live webinar included' },
        { included: false, text: 'No meal or activity planners' },
      ],
    },
  },
  'Administrator|recertification_20hr_live': {
    hero: {
      heading: 'Your 20-hour live path',
      subtitle: 'Live-instructor CEUs to complete your license cycle.',
    },
    summaryLabel: '20-Hour Live Recertification',
    oneTime: {
      header: '20-Hour Live Webinar Bundle',
      subheader: ({ facilityCode }) =>
        `Attend the 4-day live curriculum with CA-licensed faculty for ${facilityCode || 'RCFE / ARF'} Administrator recertification.`,
      priceSuffix: 'one-time',
      featuresLabel: "WHAT'S INCLUDED",
      features: [
        { included: true,  text: '20 hrs Live', detail: 'instructor-led webinars' },
        { included: true,  text: 'CA-licensed faculty', detail: 'real-time Q&A' },
        { included: true,  text: 'CDSS Approved', detail: 'meets Title 22 requirements' },
        { included: false, text: 'No self-paced portion' },
        { included: false, text: 'No meal or activity planners' },
      ],
    },
  },
};

export const TEAM_VARIANTS = {
  'Caregiver|continuing_education': {
    subscription: {
      header: 'Team Subscription – Full Access',
      subheader: 'Keep your team compliant year-round with automatic renewal and uninterrupted Annual Training access.',
      priceSuffix: 'per seat / year',
    },
    oneTime: {
      header: 'One-Time Team Training Bundle',
      subheader: 'Provide training access for your team for the current calendar year only.',
      priceSuffix: 'per seat one-time',
    },
  },
  'Caregiver|initial_training': {
    subscription: {
      header: 'Team Subscription – Full Access',
      subheader: 'Train new hires now and maintain ongoing access for future Annual Training requirements.',
      priceSuffix: 'per seat today',
    },
    oneTime: {
      header: 'One-Time Team Training Bundle',
      subheader: 'Provide one-time access for your team to complete Initial Training.',
      priceSuffix: 'per seat one-time',
    },
  },
  'Administrator|recertification': {
    subscription: {
      header: 'Team Subscription – Full Access',
      subheader: 'Help your administrators stay compliant with automatic renewal and ongoing CEU access each license cycle.',
      priceSuffix: 'per admin / license cycle',
    },
    oneTime: {
      header: 'One-Time Team CEU Bundle',
      subheader: 'Provide CEU access for your team’s current license cycle only.',
      priceSuffix: 'per admin one-time',
    },
  },
  'Administrator|initial_certification': {
    subscription: {
      header: 'Team Subscription – Full Access',
      subheader: "Your team's annual subscription includes the required Initial Certification training and continued access to Title 22 CEUs for upcoming recertification cycles.",
      priceSuffix: 'per seat today',
    },
    oneTime: {
      header: 'One-Time Team CEU Bundle',
      subheader: "Complete your team's Initial Certification with 12-month access. Continued CEU access is not included.",
      priceSuffix: 'per admin one-time',
    },
  },
  'Administrator|recertification_20hr': {
    oneTime: {
      header: 'One-Time Team CEU Bundle (20h)',
      subheader: ({ facilityCode }) =>
        `20 hours of approved self-paced CEU courses for ${facilityCode || 'RCFE / ARF'} Administrator recertification. One-time access. No live webinar included.`,
      priceSuffix: 'per admin one-time',
    },
  },
  'Administrator|recertification_20hr_live': {
    oneTime: {
      header: 'Team Live Webinar Bundle (20h)',
      subheader: ({ facilityCode }) =>
        `20 hours of live-instructor webinar CEUs per admin for ${facilityCode || 'RCFE / ARF'} recertification.`,
      priceSuffix: 'per admin one-time',
    },
  },
};

export function variantKey(role, trainingType) {
  return `${role}|${trainingType}`;
}

export function formatPrice(cents, currency = 'usd') {
  if (cents == null) return '-';
  const amount = cents / 100;
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}
